// src/views/examples/AddCustomer.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  ModalFooter,
} from "reactstrap";
import Header from "components/Headers/Header";
import Swal from "sweetalert2";
import axios from "axios";
import { useSessionStore } from "../stores/sessionStore";

// ✅ BASE จาก env
const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");
const API_CUSTOMERS = `${API_BASE}/customers`;
const API_TEST_CONNECTION = `${API_BASE}/customers/test-connection`; // ✅ ถ้า backend คุณใช้เส้นอื่น ให้แก้บรรทัดนี้

const AddCustomer = () => {
  const navigate = useNavigate();

  // ✅ actor header: usernameคนที่สร้าง
  const actor = useSessionStore((s) => s.profile?.username);

  const [submitting, setSubmitting] = useState(false);

  const [testing, setTesting] = useState(false);
  const [testStatus, setTestStatus] = useState(null); // null | success | fail

  const [form, setForm] = useState({
    customerName: "",
    status: "ACTIVE",
    notes: "",

    connectionName: "",
    host: "",
    port: "1433",
    dbName: "",
    username: "",
    password: "",
  });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const handleCancel = () => navigate("/customer");

  const handleTestConnection = async () => {
    try {
      if (!API_BASE) throw new Error("ไม่ได้ตั้งค่า REACT_APP_API_BASE ในไฟล์ .env");
      if (testing) return;

      // validate
      if (!form.host.trim()) throw new Error("กรุณากรอก Host");
      if (!String(form.port).trim()) throw new Error("กรุณากรอก Port");
      if (!form.dbName.trim()) throw new Error("กรุณากรอก DB Name");
      if (!form.username.trim()) throw new Error("กรุณากรอก Username");
      if (!form.password) throw new Error("กรุณากรอก Password");

      setTesting(true);
      setTestStatus(null);

      const payload = {
        host: form.host.trim(),
        port: Number(form.port),
        databaseName: form.dbName.trim(),
        username: form.username.trim(),
        password: form.password,
      };

      const res = await axios.post(API_TEST_CONNECTION, payload, {
        headers: {
          "Content-Type": "application/json",
          ...(actor ? { "x-actor": actor } : {}),
        },
      });

      const ok =
        (res?.data?.statustext || "").toUpperCase() === "SUCCESS" ||
        res?.data?.ok === true;

      if (!ok) throw new Error(res?.data?.message || "เชื่อมต่อไม่สำเร็จ");

      setTestStatus("success");
      await Swal.fire({
        icon: "success",
        title: "เชื่อมต่อสำเร็จ",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      setTestStatus("fail");
      await Swal.fire({
        icon: "error",
        title: "Connection Failed",
        text:
          err?.response?.data?.message ||
          err?.message ||
          "ทดสอบการเชื่อมต่อไม่สำเร็จ",
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    try {
      if (!API_BASE) throw new Error("ไม่ได้ตั้งค่า REACT_APP_API_BASE ในไฟล์ .env");
      if (!actor) throw new Error("ไม่พบ username ผู้สร้าง (x-actor)");

      // validate
      if (!form.customerName.trim()) throw new Error("กรุณากรอก Customer Name");
      if (!form.notes.trim()) throw new Error("กรุณากรอก Detail");
      if (!form.connectionName.trim())
        throw new Error("กรุณากรอก Connection Name");
      if (!form.host.trim()) throw new Error("กรุณากรอก Host");
      if (!String(form.port).trim()) throw new Error("กรุณากรอก Port");
      if (!form.dbName.trim()) throw new Error("กรุณากรอก Database Name");
      if (!form.username.trim()) throw new Error("กรุณากรอก Username");
      if (!form.password) throw new Error("กรุณากรอก Password");

      setSubmitting(true);

      const payload = {
        customerName: form.customerName.trim(),
        status: form.status || "ACTIVE",
        notes: form.notes.trim(),
        connection: {
          connectionName: form.connectionName.trim(),
          host: form.host.trim(),
          port: Number(form.port),
          databaseName: form.dbName.trim(),
          username: form.username.trim(),
          password: form.password,
          isActive: true,
        },
      };

      const res = await axios.post(API_CUSTOMERS, payload, {
        headers: {
          "Content-Type": "application/json",
          "x-actor": actor,
        },
      });

      if (res?.data?.ok === false) {
        throw new Error(res?.data?.message || "บันทึกลูกค้าไม่สำเร็จ");
      }

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Successful customer record",
        showConfirmButton: false,
        timer: 1200,
      });

      navigate("/customer");
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message ||
          err?.message
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col xs="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h6 className="mb-0">Add Customer</h6>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <Form autoComplete="off" onSubmit={handleSubmit}>
                  <div className="pl-lg-4">
                    <h6 className="heading-small text-muted mb-4">
                      Customer Information
                    </h6>

                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <label className="form-control-label required">
                            Customer Name
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            name="customerName"
                            value={form.customerName}
                            onChange={onChange}
                            required
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <label className="form-control-label required">
                            Detail
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="textarea"
                            name="notes"
                            value={form.notes}
                            onChange={onChange}
                            rows={4}
                            style={{ resize: "vertical" }}
                            required
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <hr className="my-4" />

                    <h6 className="heading-small text-muted mb-4">
                      Database Connection
                    </h6>

                    <Row>
                      <Col lg="4">
                        <FormGroup>
                          <label className="form-control-label required">
                            Connection Name
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            name="connectionName"
                            value={form.connectionName}
                            onChange={onChange}
                            required
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="4">
                        <FormGroup>
                          <label className="form-control-label required">
                            Host
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            name="host"
                            value={form.host}
                            onChange={onChange}
                            required
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="4">
                        <FormGroup>
                          <label className="form-control-label required">
                            Port
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="number"
                            name="port"
                            value={form.port}
                            onChange={onChange}
                            required
                            min={1}
                            max={65535}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="4">
                        <FormGroup>
                          <label className="form-control-label required">
                            Database Name
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            name="dbName"
                            value={form.dbName}
                            onChange={onChange}
                            required
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="4">
                        <FormGroup>
                          <label className="form-control-label required">
                            Username
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            name="username"
                            value={form.username}
                            onChange={onChange}
                            required
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="4">
                        <FormGroup>
                          <label className="form-control-label required">
                            Password
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="password"
                            name="password"
                            value={form.password}
                            onChange={onChange}
                            required
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <ModalFooter className="px-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
                      {/* ✅ ซ้าย: test connection */}
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          color="info"
                          type="button"
                          onClick={handleTestConnection}
                          disabled={testing}
                        >
                          {testing ? "กำลังทดสอบ..." : "Test Connection"}
                        </Button>

                        {testStatus === "success" && (
                          <span style={{ color: "#2dce89", fontSize: 20 }}>
                            ✔️
                          </span>
                        )}

                        {testStatus === "fail" && (
                          <span style={{ color: "#f5365c", fontSize: 20 }}>
                            ❌
                          </span>
                        )}
                      </div>

                      {/* ✅ ขวา: action buttons */}
                      <div>
                        <Button
                          color="secondary"
                          onClick={handleCancel}
                          type="button"
                          className="mr-2"
                          disabled={submitting || testing}
                        >
                          Back
                        </Button>

                        <Button
                          color="primary"
                          disabled={submitting || testing}
                          type="submit"
                        >
                          {submitting ? "Submitting..." : "Submit"}
                        </Button>
                      </div>
                    </ModalFooter>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AddCustomer;
